import dayjs from 'dayjs';
import minMax from "dayjs/plugin/minMax";
import { getOffers } from "../../../app/api/getOffers";
import { BaseAction } from "../../../core/actions/BaseAction";
import { uuid } from "../../../core/utils/Uuid";
import { convertISO8601ToDuration, convertISO8601ToMinutes, convertTimeToArabic, determineDefaultAge, determinePassengerTypeFromPassenger, extractStops, getNextDays, getPassengerCard, shouldSharePlaceWithGuardian } from "../../../Lib/Utils/validationUtil";
import { getRecoilStateValue, getRecoilStateValueWithType, updateRecoilState } from "../../../Orchestrator/Recoils/Events/Recoil";
import { clearBookingFlowAtoms } from "../../../Orchestrator/Recoils/clearBookingFlowAtoms";
import { clearOtherFlowFlags } from "../../../Lib/Utils/flowFlags";
import { IApiResponse, ILeg, ILoyaltyProgramPostResponse, ILoyaltyProgramResponse, ILoyaltyTier, IOffer, IOffersResponse, IPassengerCard, IPassengerSpecification, ITrip, IUserResponse, OffersRequest, SearchRequestType } from "../../../Orchestrator/Types/Index";
import { TripOfferModel } from "../../../Orchestrator/Types/Types/OffersResponse";
import { ICmsServiceClassFeatureList } from "../../../Orchestrator/Types/Interface/ICmsServiceClassFeatureList";
import { DynamicPriceCategory, PriceInfo, TrainType } from "../../../Orchestrator/Types/Types/TrainTypes";
import { DefaultEnum, enumPassType, PassengerPrmNeedTypeEnum, ProductCodeEnum, SHUTTLE_PRODUCT_CODE, ServiceClassTypeEnum, TrainTypeEnum, TripTypeEnum } from '../../../Orchestrator/Types/Enums/globalDefaultEnum';
import stopManager from '../../../Lib/Utils/StopManager';
import { getLoyaltyProgram } from '../../../app/api/getLoyaltyProgram';
import { compareByOrderThenPrice } from '../../../Lib/Utils/productOrderTag';
import { dropOverlappingOffers } from '../../../Lib/Utils/offerCoverage';

dayjs.extend(minMax);

function escapeRegExp(value: string): string {
    return value.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
}

/**
 * Strips the service-class prefix from a product name when (and only when) that
 * prefix matches a service-class display name configured in the CMS
 * (`service_class_feature_list[].name`, e.g. "Comfort"). Nothing else is removed,
 * so a product like "Standard Saver" stays "Standard Saver" unless the CMS name
 * for that class is literally "Standard".
 * e.g. with CMS name "Comfort": "Comfort Neighbour Free Duo (En)" → "Neighbour Free Duo (En)"
 */
export function stripServiceClassPrefix(name: string): string {
    if (!name) return "";
    const serviceClasses =
        getRecoilStateValue<ICmsServiceClassFeatureList>("cmsServiceClassFeatureListAtom") ?? [];
    const prefixes = serviceClasses
        .map(serviceClass => serviceClass?.name)
        .filter((value): value is string => Boolean(value));
    if (prefixes.length === 0) return name.trim();
    const pattern = prefixes
        // longest first so e.g. "Comfort Pro" wins over "Comfort"
        .sort((a, b) => b.length - a.length)
        .map(escapeRegExp)
        .join("|");
    return name.replace(new RegExp(`^(?:${pattern})\\s+`, "i"), "").trim();
}

export function processOffers(trips: ITrip[], offers: IOffer[]): TripOfferModel[] {
    const tripOffers: TripOfferModel[] = [];
    const now = new Date();

    for (const trip of trips) {
        const tripStartTime = new Date(trip.startTime);

        // Skip trips that have already started today
        if (tripStartTime < now) {
            continue;
        }

        // Skip trips that are cancelled
        if (trip.tripStatus?.cancelled == true) {
            continue;
        }

        let shuttleOffer: IOffer | undefined;
        let standardOffer: IOffer | undefined;
        let standardProOffer: IOffer | undefined;
        let premiumOffer: IOffer | undefined;
        let standardDuoOffer: IOffer | undefined;
        let standardQuadOffer: IOffer | undefined;
        let premiumDuoOffer: IOffer | undefined;
        let premiumQuadOffer: IOffer | undefined;

        // New arrays to support multiple offers per category
        const shuttleOffers: IOffer[] = [];
        const standardOffers: IOffer[] = [];
        const standardProOffers: IOffer[] = [];
        const premiumOffers: IOffer[] = [];
        const standardDuoOffers: IOffer[] = [];
        const standardQuadOffers: IOffer[] = [];
        const premiumDuoOffers: IOffer[] = [];
        const premiumQuadOffers: IOffer[] = [];

        // Dynamic grouping by service class (INDIVIDUAL offers only)
        const standardIndividualOffers: Map<string, IOffer[]> = new Map();
        const premiumIndividualOffers: Map<string, IOffer[]> = new Map();

        for (const offer of offers) {
            const coveredTripId = offer.tripCoverage?.coveredTripId;
            const coveredLegIds = offer.tripCoverage?.coveredLegIds || [];

            // Check if the trip ID matches or any leg ID matches
            const isMatchingTrip = coveredTripId === trip.id;
            const isMatchingLeg = trip.legs.some(leg => coveredLegIds.includes(leg.id));

            if (isMatchingTrip && isMatchingLeg) {
                const productCode = offer.products[0].code;
                const serviceClassType = offer.products?.[0]?.serviceClass?.type || offer.offerSummary?.overallServiceClass?.type;
                const offerMode = offer.admissionOfferParts?.[0]?.offerMode;

                // Keep existing switch for backward compat + compartments
                switch (productCode) {
                    case SHUTTLE_PRODUCT_CODE:
                        shuttleOffer = shuttleOffer || offer;
                        shuttleOffers.push(offer);
                        break;
                    case ProductCodeEnum.SFC:
                        standardOffer = standardOffer || offer;
                        standardOffers.push(offer);
                        break;
                    case ProductCodeEnum.SFF:
                        standardProOffer = standardProOffer || offer;
                        standardProOffers.push(offer);
                        break;
                    case ProductCodeEnum.PFF:
                        premiumOffer = premiumOffer || offer;
                        premiumOffers.push(offer);
                        break;
                    case ProductCodeEnum.STDNFDUO:
                        standardDuoOffer = standardDuoOffer || offer;
                        standardDuoOffers.push(offer);
                        break;
                    case ProductCodeEnum.STDNFQUAD:
                        standardQuadOffer = standardQuadOffer || offer;
                        standardQuadOffers.push(offer);
                        break;
                    case ProductCodeEnum.PREMNFDUO:
                        premiumDuoOffer = premiumDuoOffer || offer;
                        premiumDuoOffers.push(offer);
                        break;
                    case ProductCodeEnum.PREMNFQUAD:
                        premiumQuadOffer = premiumQuadOffer || offer;
                        premiumQuadOffers.push(offer);
                        break;
                }

                // Dynamic grouping: INDIVIDUAL offers by service class
                if (productCode !== SHUTTLE_PRODUCT_CODE && offerMode !== "COLLECTIVE") {
                    const targetMap = serviceClassType === ServiceClassTypeEnum.HIGH
                        ? premiumIndividualOffers
                        : standardIndividualOffers;
                    if (!targetMap.has(productCode)) {
                        targetMap.set(productCode, []);
                    }
                    targetMap.get(productCode)!.push(offer);
                }
            }
        }

        // Every offer kept in a bucket is booked (and charged). Drop same-product offers that
        // re-sell a (leg, passenger) an earlier offer already covers, so one seat is booked once.
        for (const bucket of [shuttleOffers, standardOffers, standardProOffers, premiumOffers, standardDuoOffers, standardQuadOffers, premiumDuoOffers, premiumQuadOffers]) {
            bucket.splice(0, bucket.length, ...dropOverlappingOffers(bucket));
        }
        for (const individualMap of [standardIndividualOffers, premiumIndividualOffers]) {
            individualMap.forEach((bucket, productCode) => individualMap.set(productCode, dropOverlappingOffers(bucket)));
        }

        if (trip?.legs?.length > 0) {
            trip.legs = trip?.legs?.map((leg: ILeg) => {
                const legOffers: IOffer[] | undefined = offers.filter(offer => offer?.tripCoverage?.coveredTripId === trip.id && offer.tripCoverage?.coveredLegIds?.includes(leg.id));
                if (legOffers) {
                    leg.legOffer = [...legOffers];
                }
                return leg;
            })
        }

        const tripOfferEntry: TripOfferModel = {
            trip,
            shuttleOffer,
            standardOffer,
            standardProOffer,
            premiumOffer,
            standardDuoOffer,
            standardQuadOffer,
            premiumDuoOffer,
            premiumQuadOffer,
            standardIndividualOffers,
            premiumIndividualOffers
        };

        const legs = trip?.legs || [];
        const shuttleLegIds = legs.filter(l => l?.timedLeg?.service?.mode?.ptMode === "BUS").map(l => l.id);
        const trainLegIds = legs.filter(l => l?.timedLeg?.service?.mode?.ptMode !== "BUS").map(l => l.id);
        const hasAllIds = (offers: IOffer[]) => offers.length > 0 && offers.every(o => !!o?.offerId);
        const coversAllLegs = (offers: IOffer[], requiredLegIds: string[]) => {
            if (!hasAllIds(offers) || requiredLegIds.length === 0) return false;
            const covered = new Set<string>();
            offers.forEach(o => (o?.tripCoverage?.coveredLegIds || []).forEach((id: string) => covered.add(id)));
            return requiredLegIds.every(id => covered.has(id));
        };
        if (coversAllLegs(shuttleOffers, shuttleLegIds)) tripOfferEntry.shuttleOffers = shuttleOffers;
        if (coversAllLegs(standardOffers, trainLegIds)) tripOfferEntry.standardOffers = standardOffers;
        if (coversAllLegs(standardProOffers, trainLegIds)) tripOfferEntry.standardProOffers = standardProOffers;
        if (coversAllLegs(premiumOffers, trainLegIds)) tripOfferEntry.premiumOffers = premiumOffers;
        if (coversAllLegs(standardDuoOffers, trainLegIds)) tripOfferEntry.standardDuoOffers = standardDuoOffers;
        if (coversAllLegs(standardQuadOffers, trainLegIds)) tripOfferEntry.standardQuadOffers = standardQuadOffers;
        if (coversAllLegs(premiumDuoOffers, trainLegIds)) tripOfferEntry.premiumDuoOffers = premiumDuoOffers;
        if (coversAllLegs(premiumQuadOffers, trainLegIds)) tripOfferEntry.premiumQuadOffers = premiumQuadOffers;
        tripOffers.push(tripOfferEntry);
    }

    return tripOffers;
}

export function convertOffersToTrains(offersData: TripOfferModel[], type: string = TrainTypeEnum.outbound): TrainType[] {
    if (!offersData || offersData.length === 0) return [];
    const extractedTrains: TrainType[] = [];
    let idCounter = 1;
    offersData.forEach((offerModel) => {
        const trip: ITrip = offerModel.trip;
        
        // Handle delayed trips - use observed times
        let observedStartTime: string | undefined;
        let observedEndTime: string | undefined;
        let departureTimeSource: string;
        let arrivalTimeSource: string;
        
        if (trip.tripStatus?.delayed == true) {
            const firstLeg = trip?.legs[0];
            const lastLeg = trip?.legs[trip?.legs?.length - 1];
            
            // For delayed trips: use observed times for departure/arrival
            departureTimeSource = firstLeg?.timedLeg?.start?.serviceDeparture?.observedTime || trip.startTime;
            arrivalTimeSource = lastLeg?.timedLeg?.end?.serviceArrival?.observedTime || trip.endTime;
            
            // Store original trip times as observed times
            observedStartTime = trip.startTime;
            observedEndTime = trip.endTime;
        } else {
            // For non-delayed trips: use trip times as before
            departureTimeSource = trip.startTime;
            arrivalTimeSource = trip.endTime;
        }
        
        const departureTime = convertTimeToArabic(departureTimeSource);
        const arrivalTime = convertTimeToArabic(arrivalTimeSource);
        const duration = convertISO8601ToDuration(trip.duration);
        const durationInMinutes = convertISO8601ToMinutes(trip.duration);
        const stops = extractStops(trip);
        const fromName = stopManager.getStopNameById(trip.origin.stopPlaceRef);
        const toName = stopManager.getStopNameById(trip.destination.stopPlaceRef);
        const trainNumbers = trip.legs[0].timedLeg.service.vehicleNumbers.join(", ");
        const transfers = trip?.transfers || 0;
        const shortDate = departureTimeSource || ""
        const soldOutDefault = { price: 0, isSoldOut: true, discount: undefined, seatsLeft: undefined, offerId: "", originalPrice: undefined, descriptionText: "" };
        const priceInfo: PriceInfo = {
            standardProducts: [],
            premiumProducts: [],
            shuttle: { ...soldOutDefault },
            standardValue: { ...soldOutDefault },
            standardPro: { ...soldOutDefault },
            premium: { ...soldOutDefault },
            standardDuo: { ...soldOutDefault },
            standardQuad: { ...soldOutDefault },
            premiumDuo: { ...soldOutDefault },
            premiumQuad: { ...soldOutDefault },
        };

        const shuttleOffersRaw = offerModel.shuttleOffers || (offerModel.shuttleOffer ? [offerModel.shuttleOffer] : []);
        const standardOffersRaw = offerModel.standardOffers || (offerModel.standardOffer ? [offerModel.standardOffer] : []);
        const standardProOffersRaw = offerModel.standardProOffers || (offerModel.standardProOffer ? [offerModel.standardProOffer] : []);
        const premiumOffersRaw = offerModel.premiumOffers || (offerModel.premiumOffer ? [offerModel.premiumOffer] : []);
        const standardDuoOffersRaw = offerModel.standardDuoOffers || (offerModel.standardDuoOffer ? [offerModel.standardDuoOffer] : []);
        const standardQuadOffersRaw = offerModel.standardQuadOffers || (offerModel.standardQuadOffer ? [offerModel.standardQuadOffer] : []);
        const premiumDuoOffersRaw = offerModel.premiumDuoOffers || (offerModel.premiumDuoOffer ? [offerModel.premiumDuoOffer] : []);
        const premiumQuadOffersRaw = offerModel.premiumQuadOffers || (offerModel.premiumQuadOffer ? [offerModel.premiumQuadOffer] : []);
        const legs = trip?.legs || [];
        const shuttleLegIds = legs.filter(l => l?.timedLeg?.service?.mode?.ptMode === "BUS").map(l => l.id);
        const trainLegIds = legs.filter(l => l?.timedLeg?.service?.mode?.ptMode !== "BUS").map(l => l.id);

        const hasAllIds = (offers: IOffer[]) => offers.length > 0 && offers.every(o => !!o?.offerId);
        const coversAllLegs = (offers: IOffer[], requiredLegIds: string[]) => {
            if (!hasAllIds(offers) || requiredLegIds.length === 0) return false;
            const covered = new Set<string>();
            offers.forEach(o => (o?.tripCoverage?.coveredLegIds || []).forEach((id: string) => covered.add(id)));
            return requiredLegIds.every(id => covered.has(id));
        };

        const shuttleOffers = coversAllLegs(shuttleOffersRaw, shuttleLegIds) ? shuttleOffersRaw : [];
        const standardOffers = coversAllLegs(standardOffersRaw, trainLegIds) ? standardOffersRaw : [];
        const standardProOffers = coversAllLegs(standardProOffersRaw, trainLegIds) ? standardProOffersRaw : [];
        const premiumOffers = coversAllLegs(premiumOffersRaw, trainLegIds) ? premiumOffersRaw : [];
        const standardDuoOffers = coversAllLegs(standardDuoOffersRaw, trainLegIds) ? standardDuoOffersRaw : [];
        const standardQuadOffers = coversAllLegs(standardQuadOffersRaw, trainLegIds) ? standardQuadOffersRaw : [];
        const premiumDuoOffers = coversAllLegs(premiumDuoOffersRaw, trainLegIds) ? premiumDuoOffersRaw : [];
        const premiumQuadOffers = coversAllLegs(premiumQuadOffersRaw, trainLegIds) ? premiumQuadOffersRaw : [];
        const aggregateOffers = (offers: IOffer[] | undefined) => {
            if (!offers || offers.length === 0) return null;
            let totalBasePrice = 0;
            let totalOriginalPrice = 0;
            let seatsLeft: number | undefined = undefined;
            const offerIds: string[] = [];
            const descriptions: string[] = [];

            offers.forEach(o => {
                const parts = o?.admissionOfferParts || [];
                const firstScale = parts?.[0]?.price?.scale || 1;
                const base = parts.reduce((sum, p) => sum + (p?.price?.amount || 0), 0) / ((100 * firstScale) / firstScale);
                const original = parts.reduce((sum, p) => sum + (p?.originalPrice?.amount || 0), 0) / ((100 * firstScale) / firstScale);
                totalBasePrice += base;
                totalOriginalPrice += original;

                const availability = (o?.reservationOfferParts?.[0] as any)?.numericAvailability;
                if (typeof availability === "number") {
                    seatsLeft = typeof seatsLeft === "number" ? Math.min(seatsLeft, availability) : availability;
                }
                if (o?.offerId) offerIds.push(o.offerId);
                const desc = o?.products?.[0]?.descriptiveTexts?.[0]?.description;
                if (desc) descriptions.push(desc);
            });

            const discount = offers[0]?.admissionOfferParts?.[0]?.reductionSummary?.[0]?.discountPercentage;
            const firstProduct = offers[0]?.products?.[0];
            const firstOffer = offers[0];
            return {
                price: totalBasePrice,
                isSoldOut: offerIds.length === 0,
                discount: discount || undefined,
                seatsLeft: seatsLeft,
                offerId: offerIds,
                basePrice: totalBasePrice,
                originalPrice: totalOriginalPrice,
                descriptionText: descriptions[0],
                productName: firstProduct?.summary || firstProduct?.description || "",
                descriptiveTexts: firstProduct?.descriptiveTexts || [],
                isRecommended: (firstProduct?.productTags || []).some((tag: string) => tag?.toUpperCase()?.includes("RECOMMEND")),
            } as any;
        };

        const aggShuttle = aggregateOffers(shuttleOffers);
        const aggStandard = aggregateOffers(standardOffers);
        const aggStandardPro = aggregateOffers(standardProOffers);
        const aggPremium = aggregateOffers(premiumOffers);
        const aggStandardDuo = aggregateOffers(standardDuoOffers);
        const aggStandardQuad = aggregateOffers(standardQuadOffers);
        const aggPremiumDuo = aggregateOffers(premiumDuoOffers);
        const aggPremiumQuad = aggregateOffers(premiumQuadOffers);
        if (aggShuttle) priceInfo.shuttle = aggShuttle;
        if (aggStandard) priceInfo.standardValue = aggStandard;
        if (aggStandardPro) priceInfo.standardPro = aggStandardPro;
        if (aggPremium) priceInfo.premium = aggPremium;
        if (aggStandardDuo) priceInfo.standardDuo = aggStandardDuo;
        if (aggStandardQuad) priceInfo.standardQuad = aggStandardQuad;
        if (aggPremiumDuo) priceInfo.premiumDuo = aggPremiumDuo;
        if (aggPremiumQuad) priceInfo.premiumQuad = aggPremiumQuad;

        // Build dynamic product arrays from service class grouping
        const stdIndividualMap = offerModel.standardIndividualOffers;
        const premIndividualMap = offerModel.premiumIndividualOffers;

        const buildDynamicProducts = (offerMap: Map<string, IOffer[]> | undefined, serviceClassType: string): DynamicPriceCategory[] => {
            if (!offerMap || offerMap.size === 0) return [];
            const products: DynamicPriceCategory[] = [];
            offerMap.forEach((offersForCode, productCode) => {
                // Only include offers that cover all required train legs
                const validOffers = coversAllLegs(offersForCode, trainLegIds) ? offersForCode : [];
                const agg = aggregateOffers(validOffers);
                if (agg) {
                    const firstOffer = offersForCode[0];
                    const firstProduct = firstOffer?.products?.[0];
                    products.push({
                        ...agg,
                        productCode,
                        productName: firstProduct?.summary || firstProduct?.description || "",
                        serviceClassType,
                        descriptiveTexts: firstProduct?.descriptiveTexts || [],
                        isRecommended: (firstProduct?.productTags || []).some((tag: string) => tag?.toUpperCase()?.includes("RECOMMEND")),
                        productTags: firstProduct?.productTags || [],
                    });
                }
            });
            // Sort by `order` product tag (CMS-curated display order) first,
            // falling back to price ascending when the tag is absent.
            products.sort(compareByOrderThenPrice);
            return products;
        };

        priceInfo.standardProducts = buildDynamicProducts(stdIndividualMap, ServiceClassTypeEnum.STANDARD);
        priceInfo.premiumProducts = buildDynamicProducts(premIndividualMap, ServiceClassTypeEnum.HIGH);
        const train: TrainType = {
            id: idCounter++,
            departureTime,
            arrivalTime,
            duration,
            durationInMinutes,
            isSoldOut: priceInfo.standardProducts.length === 0 && priceInfo.premiumProducts.length === 0 && priceInfo.standardValue.isSoldOut && priceInfo.standardPro.isSoldOut && priceInfo.premium.isSoldOut,
            stops,
            trainNumbers,
            priceInfo,
            type: type,
            selectedPriceClass: null,
            transfers,
            fromName,
            toName,
            shortDate,
            legs: trip.legs
        };
        
        // Add observed times if trip is delayed
        if (observedStartTime) {
            (train as TrainType).observedStartTime = convertTimeToArabic(observedStartTime);
        }
        if (observedEndTime) {
            (train as TrainType).observedEndTime = convertTimeToArabic(observedEndTime);
        }
        
        // Attach the full arrays for consumers needing multi-offer data
        if (coversAllLegs(shuttleOffers, shuttleLegIds)) train.shuttleOffers = shuttleOffers;
        if (coversAllLegs(standardOffers, trainLegIds)) train.standardOffers = standardOffers;
        if (coversAllLegs(standardProOffers, trainLegIds)) train.standardProOffers = standardProOffers;
        if (coversAllLegs(premiumOffers, trainLegIds)) train.premiumOffers = premiumOffers;
        if (coversAllLegs(standardDuoOffers, trainLegIds)) train.standardDuoOffers = standardDuoOffers;
        if (coversAllLegs(standardQuadOffers, trainLegIds)) train.standardQuadOffers = standardQuadOffers;
        if (coversAllLegs(premiumDuoOffers, trainLegIds)) train.premiumDuoOffers = premiumDuoOffers;
        if (coversAllLegs(premiumQuadOffers, trainLegIds)) train.premiumQuadOffers = premiumQuadOffers;
        extractedTrains.push(train);
    });
    return extractedTrains;
}

class GetSearchResultAction extends BaseAction<{ result: string, data: any }, SearchRequestType> {

    protected override deduplicate = true;

    getStopDuration(nextLeg: any, endStop: any): string {
        if (!nextLeg?.timedLeg?.start?.serviceDeparture?.timetabledTime ||
            !endStop?.serviceArrival?.timetabledTime) {
            return "";
        }

        const nextDepartureTime = new Date(nextLeg.timedLeg.start.serviceDeparture.timetabledTime);
        const currentArrivalTime = new Date(endStop.serviceArrival.timetabledTime);

        const durationInMinutes = Math.floor((nextDepartureTime.getTime() - currentArrivalTime.getTime()) / (1000 * 60));
        return `${durationInMinutes}min`;
    }

    private storedPassengerRefs: {
        [key: string]: {
            externalRef: string;
            cards?: IPassengerCard[];
        }
    } = {};

    // Overriding `run` and returning the transformed stops
    override async run(): Promise<{ result: string, data: any }> {
        try {
            const { departureTime, arrivalTime, origin, destination, passengerCounts, trainType, selectedTrain, resetValue, fromShuttle, toShuttle, selectedTripPassenger, isFromPopUp, isOnlyOfferReturn = false, isFromBookItAgain = false, isFromBookReturnJourney = false, isPassBookingFlow = false, prefillSelectedTripPassenger = false } = this.params!;
            if (resetValue) {
                // A fresh search starts a brand-new booking flow. Wipe stale
                // flow state from any prior aborted checkout (e.g. white-page
                // redirect failure) before any of this flow's writes run.
                // Skip when callers (BookItAgain, BookReturnJourney, pass flow)
                // have pre-seeded atoms that this action expects to keep.
                if (!isFromBookItAgain && !isFromBookReturnJourney && !isPassBookingFlow) {
                    clearBookingFlowAtoms();
                }
                // The localStorage twins of the flow atoms cleared above, and the
                // same argument applies: a fresh search is a new booking, so an
                // aftersales marker left by an earlier flow must not survive into
                // it. Unguarded, unlike the atom clear — those callers pre-seed
                // ATOMS that clearBookingFlowAtoms would wipe, while none of them
                // has any use for a stale change-date marker.
                //
                // Load-bearing beyond the wrong-toast case flowFlags.ts describes.
                // PrepareBookingSummaryAction gates its whole normal-checkout
                // branch on CHANGE_DATE_FLOW: with the flag stuck "true", every
                // later booking ran the action and silently rebuilt nothing, so
                // checkoutPaymentSummaryAtom kept its previous contents. Measured
                // on INT 2026-08-26: change the date on a booking, then start a
                // new one and add a shuttle — POST /booked-offers returns 200 and
                // the offer is on the booking, but the receipt draws no shuttle
                // row and the total stays at the train fare. It survived reloads
                // and new bookings because it is localStorage, and cleared only on
                // "clear site data", which is what made it look like a cache bug.
                clearOtherFlowFlags();
                updateRecoilState("paymentOutboundAtom", []);
                updateRecoilState("paymentInboundAtom", []);
                updateRecoilState("selectedOutboundTrainAtom", null);
                updateRecoilState("selectedInboundTrainAtom", null);
                updateRecoilState("trainTypeAtom", TrainTypeEnum.outbound);
                // Reset the payment state atom to its initial state
                updateRecoilState("paymentStateAtom", {
                    method: null,
                    total: 0,
                });
                // Reset train legs to prevent stale inbound data from previous booking flow
                updateRecoilState("selectedTrainLegsAtom", {
                    outbound: [],
                    inbound: [],
                });
            }
            
            //Fetch and store the loyalty program when new search is made by the logged in user
            const isAnonymous: boolean = getRecoilStateValue("isAnonymousStateAtom") as boolean;
            const userData: IUserResponse = getRecoilStateValue("userDataAtom") as IUserResponse;
            if (isAnonymous === false && userData && userData.id) {
                const loyaltyProgram = await getLoyaltyProgram(userData.id);
                if (loyaltyProgram.status === "ok" && loyaltyProgram.answer) {
                    updateRecoilState<ILoyaltyProgramResponse>("loyaltyProgramResponseAtom", loyaltyProgram.answer);
                }
            }

            // if (!origin || !destination) {
            //     showNotification('error', 'Incomplete Selection', 'Please Select Origin & Destination first.');
            //     return "false";
            // }
            // Generate passengerRefs and passengerSpecifications based on totalPassengerCount
            const passengerRefs: string[] = [];
            const passengerSpecifications = [];
            const passengerSpecificationsTypes = [];
            for (const [passengerType, count] of Object.entries(passengerCounts)) {
                for (let i = 0; i < (Number(count)); i++) {
                    let ref: string;

                    // Generate a key for this passenger
                    const passengerKey = `${passengerType}_${i}`;

                    // Check if we already have a ref for this passenger type and index
                    if (this.storedPassengerRefs[passengerKey]) {
                        ref = this.storedPassengerRefs[passengerKey].externalRef;
                    } else if (isPassBookingFlow && selectedTripPassenger?.length) {
                        const matchPassengers = selectedTripPassenger.filter(passenger => {
                            return determinePassengerTypeFromPassenger(passenger) === passengerType
                        });

                        ref = matchPassengers?.[i]?.externalRef || uuid();
                        this.storedPassengerRefs[passengerKey] = {
                            externalRef: ref,
                        };
                    } else {
                        // Generate new ref and store it
                        ref = uuid();
                        this.storedPassengerRefs[passengerKey] = {
                            externalRef: ref,
                        };
                    }

                    passengerRefs.push(ref);
                    passengerSpecifications.push({
                        externalRef: ref,
                        age: determineDefaultAge(passengerType),
                        type: 'PERSON',
                        cards: [
                            ...getPassengerCard(passengerType),
                            ...(this.storedPassengerRefs[passengerKey].cards || [])
                        ],
                        ...(passengerType === "wheelchair" ? {
                            prmNeeds: [PassengerPrmNeedTypeEnum.Wheelchair]
                        } : {})
                    });
                    passengerSpecificationsTypes.push({
                        passengerType: passengerKey,
                        externalRefId: ref,
                    })
                }
            }

            const newOfferRequest: OffersRequest = {
                tripSearchCriteria: {
                    departureTime: departureTime,
                    origin: {
                        stopPlaceRef: origin,
                        objectType: "StopPlaceRef",
                    },
                    destination: {
                        stopPlaceRef: destination,
                        objectType: "StopPlaceRef",
                    },
                    // Book-return's offer is ATTACHED to an existing booking
                    // (POST /booked-offers), and the retailer refuses an
                    // inbound-stamped offer there: it stamps the minted offer from
                    // this field, then rejects it with 400 INVALID_OFFER_SELECTION
                    // — while POST /offers returns identical results for BOTH
                    // values, so the failure only surfaces at attach time.
                    // Measured on int 2026-09-02, same booking/trip/passengers,
                    // direction the only variable: IN_BOUND -> 400 (x2),
                    // OUT_BOUND -> 200, leg attached (x2). Same trap the shuttle
                    // path hard-codes around (GetShuttleE2EOffersAction).
                    //
                    // A normal round trip keeps IN_BOUND: both offers go in at
                    // booking CREATION, which the retailer accepts.
                    //
                    // The attached leg comes back stamped OUT_BOUND on the
                    // booking, so a booking that has been through this flow holds
                    // two OUT_BOUND train trips and no IN_BOUND one. Anything that
                    // splits legs by `trip.direction` mis-handles that — chiefly
                    // ConfirmCancelClickAction (refund scoping: cancelling the
                    // outbound would scope over the return leg too) and
                    // TicketsModal (draws no direction tabs, so the return ticket
                    // is unreachable). Those must be fixed before this ships.
                    direction: trainType == "inbound" && !isFromBookReturnJourney
                        ? TripTypeEnum.IN_BOUND
                        : TripTypeEnum.OUT_BOUND
                    // returnSearchParameters: arrivalTime ?
                    //     {
                    //         ...(trainType === TrainTypeEnum.outbound ?
                    //             { inwardReturnDate: arrivalTime } :
                    //             { outwardTripDate: arrivalTime }
                    //         )
                    //     } : null
                },
                offerSearchCriteria: {
                    currency: "AED",
                },
                anonymousPassengerSpecifications: []
                //selectedTripPassenger is used from book it again, this action also called from book it again
                //anonymousPassengerSpecifications: selectedTripPassenger ? selectedTripPassenger : passengerSpecifications
                //COMMENTED ABOVE LINE BCOZ FOR ROUND TRIP IT IS NOT WORKING (PRAMOD)
                //anonymousPassengerSpecifications: passengerSpecifications
            };

            if (selectedTripPassenger && isFromBookItAgain) {
                newOfferRequest.anonymousPassengerSpecifications = selectedTripPassenger
            }
            else if (selectedTripPassenger && (isFromBookReturnJourney || prefillSelectedTripPassenger)) {
                // Transform selectedTripPassenger to match the required anonymousPassengerSpecifications structure
                newOfferRequest.anonymousPassengerSpecifications = selectedTripPassenger.map((passenger) => ({
                    externalRef: passenger.externalRef,
                    age: passenger.age,
                    type: 'PERSON',
                    cards: passenger.cards,
                    prmNeeds: passenger.prmNeeds,
                }));

                // Update passengerSpecificationsTypes externalRefId with passenger.externalRef
                passengerSpecificationsTypes.forEach((specType, index) => {
                    if (selectedTripPassenger[index]) {
                        specType.externalRefId = selectedTripPassenger[index].externalRef;
                    }
                });

            }
            else {
                newOfferRequest.anonymousPassengerSpecifications = passengerSpecifications
            }

            newOfferRequest.anonymousPassengerSpecifications = newOfferRequest.anonymousPassengerSpecifications.map((passenger: any) => {
                if (shouldSharePlaceWithGuardian(passenger)) {
                    return { ...passenger, sharePlaceWithGuardian: true };
                }
                return passenger;
            });

            const appliedPassengerPassState = getRecoilStateValueWithType("appliedPassengerPassAtom");

            newOfferRequest.anonymousPassengerSpecifications = newOfferRequest.anonymousPassengerSpecifications.map((passenger) => {
                const currentTrainType = getRecoilStateValue<TrainTypeEnum>("trainTypeAtom");
                const currentTripPassOffers = currentTrainType === TrainTypeEnum.inbound ? appliedPassengerPassState?.inbound : appliedPassengerPassState?.outbound;

                const matchPassengerPassOffer = currentTripPassOffers?.find((passOffer) => {
                    return passOffer?.externalRef === passenger.externalRef;
                });
              
                let passengerPassCode: string | undefined;
                if (matchPassengerPassOffer?.passOffer?.booking?.booking?.bookedOffers) {
                    for (const bookedOffer of matchPassengerPassOffer.passOffer.booking.booking.bookedOffers) {
                        if (bookedOffer?.admissions) {
                            for (const admission of bookedOffer.admissions) {
                                if (admission?.appliedReductions) {
                                    const travelPassReduction = admission.appliedReductions.find(
                                        (reduction: any) => reduction?.type === "TRAVEL_PASS"
                                    );
                                    if (travelPassReduction?.code) {
                                        passengerPassCode = travelPassReduction.code;
                                        break;
                                    }
                                }
                            }
                            if (passengerPassCode) break;
                        }
                    }
                }
                
                if (!passengerPassCode) {
                    passengerPassCode = matchPassengerPassOffer?.passOffer.booking?.booking?.bookedOffers?.[0]?.products?.[0]?.code;
                }

                return {
                    ...passenger,
                    cards: [
                        ...(passenger.cards || []),
                        ...(matchPassengerPassOffer ? [{
                            ...(passengerPassCode && {
                                code: passengerPassCode,
                            }),
                            type: enumPassType.TRAVEL_PASS as const,
                            number: matchPassengerPassOffer?.passOffer?.travelAccount.travelAccount.number || "",
                            useConcessionVouchers: false,
                            issuer: matchPassengerPassOffer?.passOffer?.travelAccount.travelAccount.issuer || "",
                        }] : [])
                    ]
                }
            })

            updateRecoilState<OffersRequest>('offersRequestAtom', newOfferRequest);

            // Clear the trainAtom before fetching new data
            updateRecoilState("trainAtom", []);
            updateRecoilState("filterTrainsAtom", [])

            updateRecoilState<IPassengerSpecification[]>("passengerSpecificationsTypesAtom", passengerSpecificationsTypes);

            // Search paths render the offer page's empty state on backend
            // errors (app parity) — suppress ApiRequest's "No Trips Available"
            // toast for them. isOnlyOfferReturn callers keep the toast, as
            // they have no page to show instead.
            const response: IApiResponse<IOffersResponse> = await getOffers(newOfferRequest, isOnlyOfferReturn);
            let convertedTrainsData;
            // The date strip must populate regardless of the day's results —
            // it is the recovery affordance when the day is empty, sold out,
            // or the backend answered TRIP_NO_SEARCH_RESULT.
            const populateDateStrip = () => {
                const today = dayjs().startOf("day");
                if (trainType === TrainTypeEnum.outbound) {
                    const referenceDate = departureTime ? dayjs(departureTime) : today;
                    const sixtyDaysAgo = referenceDate.subtract(DefaultEnum.HorizontalCalendarDayScroll, "days").startOf("day");
                    const start = dayjs.max(sixtyDaysAgo, today.startOf("day"));  // Ensure start is not before today
                    const end = referenceDate.add(DefaultEnum.HorizontalCalendarDayScroll, "days").startOf("day");

                    updateRecoilState("availableDatesAtom", getNextDays(
                        start.format("YYYY-MM-DD"),
                        end.format("YYYY-MM-DD")
                    ));
                } else {
                    const start = dayjs(arrivalTime);
                    const end = dayjs(departureTime).add(DefaultEnum.HorizontalCalendarDayScroll, "days").startOf("day");

                    updateRecoilState("availableDatesAtom", getNextDays(
                        start.format("YYYY-MM-DD"),
                        end.format("YYYY-MM-DD")
                    ));
                }
            };
            if (response.status === "ok" && response.answer) {
                const offersData: IOffersResponse = response.answer;
                if (isOnlyOfferReturn) {

                    let responseData = {
                        result: "true",
                        data: offersData
                    }
                    return responseData;
                }
                populateDateStrip();

                // Gate on trips, not offers: a fully sold-out day returns the
                // day's trips with an empty offers array (SOLD_OUT warning in
                // the /offers swagger), and must still render the timetable
                // with every service marked sold out.
                if (offersData.trips?.length > 0) {
                    //Updates the offerAtom                    
                    updateRecoilState("offerAtom", offersData.offers);
                    updateRecoilState("tripOfferAtom", offersData.trips);
                    //#region 
                    //Process the offers to create a list of trip offers
                    const tripOffers = processOffers(offersData.trips, offersData.offers);

                    //console.log("tripOffers=>", tripOffers)
                    //Convert Offers To Trains                    
                    const convertedTrains = convertOffersToTrains(tripOffers, trainType);
                    // Filter Inbound train base on outbound train arrival time.
                    if (selectedTrain && dayjs(departureTime).isSame(dayjs(), 'day')) {
                        const outBoundArrivalTime = (selectedTrain?.arrivalTime || "0").replace(":", ".")
                        const outBoundArrivalTimeInt = parseFloat(outBoundArrivalTime)
                        //Updates the trainAtom
                        if (outBoundArrivalTimeInt > 0) {
                            const convertedTrainsList = convertedTrains.filter(train => parseFloat(train?.departureTime?.replace(":", ".")) > outBoundArrivalTimeInt)
                            updateRecoilState("trainAtom", convertedTrainsList);
                            convertedTrainsData = convertedTrainsList;
                        } else {
                            updateRecoilState("trainAtom", []);
                            convertedTrainsData = []
                        }
                    } else {
                        updateRecoilState("trainAtom", convertedTrains);
                        convertedTrainsData = convertedTrains
                    }
                    //#endregion

                    if (fromShuttle || toShuttle) {
                        if (offersData.trips.length > 0 && offersData.trips[0]?.legs?.length == 1 && offersData.trips[0]?.legs[0]?.timedLeg?.service?.mode?.ptMode == "BUS") {
                            //updateRecoilState("isShuttleBookingAtom", true);
                            //return "shuttle"
                            let responseData = {
                                result: "shuttle",
                                data: convertedTrains
                            }
                            updateRecoilState("isAddShuttleAtom", true);
                            updateRecoilState("trainShuttleBookingAtom", true);
                            return responseData;
                        }
                    } else {
                        updateRecoilState("trainShuttleBookingAtom", false);
                    }

                } else {
                    // No trips for the day: no popup (app parity) — the train
                    // list renders its own "no trains" empty state.
                    convertedTrainsData = [];
                    console.warn("No trips available.");
                }
            } else {
                // Backend "no search result" (e.g. TRIP_NO_SEARCH_RESULT 400,
                // unserved route or no timetable data): app parity — treat it
                // exactly like a day with zero trips so the offer page shows
                // its "no trains" empty state with a working date strip.
                if (!isOnlyOfferReturn) {
                    populateDateStrip();
                    convertedTrainsData = [];
                }
            }
            //return "true"
            let responseData = {
                result: "true",
                data: convertedTrainsData
            }
            return responseData;
        } catch (error: any) {
            console.error("Error fetching offers:", error);
            //return "false"
            let responseData = {
                result: "false",
                data: []
            }
            return responseData;
        }
    }
}

export const getSearchResultAction = new GetSearchResultAction()


// http://localhost:3000/bookings/details/ae5c78f3-cf21-4639-a460-ca824212c46a
